import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import Button from '../Button';
// eslint-disable-next-line import/no-cycle
import { DayButton } from '.';
import { DayPicker } from '../Calendar';
import { getPageContext } from '../../utils';
import { SEARCH_PAGE } from '../../utils/getPageContext';
import Icon, { IconLoader } from '../Icon';
import Arrow from '../../svg/icons/arrow.svg';
import Logger from '../../utils/logger';
import { getTranslation } from '../../providers/TranslationProvider';

import './ResyCalendar.scss';
import { sanitizeString } from '../../utils/sanitizeString';

IconLoader((name) => {
  const sanitizedName = sanitizeString(name);
  import(
    /* webpackChunkName: "[request]" */
    `../../svg/icons/${sanitizedName}.svg`
  ).then(
    (module) => module.default,
    () => Logger.error(`[ERROR] Icon ${name} does not exist`)
  );
});

const ResyCalendar = ({
  Analytics,
  closeCalendar,
  onClick,
  venueCity,
  calendarMap,
  getAriaLabel,
  selectedDate,
  clickableElementIdsToIgnore,
  currentMonth,
  numberOfMonths,
  focusOnClose,
  showCloseButton,
  titleText,
  searchSessionId,
}) => {
  const [hasCapture, setHasCapture] = useState(null);

  const handleDayClick = (day) => {
    const pageContext = getPageContext(window.location.pathname);
    if (venueCity !== 'Global Nav') {
      Analytics.event('complete search', {
        context: pageContext,
        'filter type': 'search filter',
        value: 'date',
        searchSessionId,
      });
    }

    if (pageContext === SEARCH_PAGE) {
      onClick(day, { usesBackdrop: false });
      closeCalendar();
    } else {
      onClick(day);
    }
  };

  const onPointerLeave = () => {
    setHasCapture(null);
  };

  const getOnPointerEnter = (val) => () => setHasCapture(val);

  const renderDayContents = (date) => {
    const { className, day, index } = date;

    const dayFormatted = day && day.format('YYYY-MM-DD');
    const dayStatus = (calendarMap && calendarMap[dayFormatted]) || 'outside-window';
    const isActive = dayFormatted === selectedDate;
    return (
      <td
        role="cell"
        className="CalendarMonth__Cell ResyCalendar__Cell"
        key={`${day}_${index}`}
      >
        <DayButton
          className={className}
          date={day}
          onClick={() => handleDayClick(day, dayStatus)}
          status={dayStatus}
          getAriaLabel={getAriaLabel}
          selected={isActive}
          id={isActive ? 'active-date' : undefined}
        />
      </td>
    );
  };

  const renderNavButton = (navProps) => {
    const ordinal = navProps.direction === 'next' ? 'right' : 'left';
    const formattedCurrentMonth = navProps.currentMonth ? navProps.currentMonth.format('MMMM YYYY') : '';
    let ariaLabelText = '';
    if (navProps.direction === 'next') {
      ariaLabelText = getTranslation({ translationKey: 'next_month', translationValues: { formattedCurrentMonth }, fallback: `Next month, ${formattedCurrentMonth} selected` });
    } else {
      ariaLabelText = getTranslation({ translationKey: 'prev_month', translationValues: { formattedCurrentMonth }, fallback: `Previous month, ${formattedCurrentMonth} selected` });
    }
    return (
      <Button
        aria-label={ariaLabelText}
        circle
        className={`ResyCalendar__nav_${ordinal}`}
        onPointerEnter={getOnPointerEnter(ordinal)}
        onPointerLeave={onPointerLeave}
        {...navProps}
      >
        <Icon name="arrow" component={Arrow} fill={hasCapture === ordinal ? '#fff' : '#4076dd'} />
      </Button>
    );
  };

  return (
    <DayPicker
      className="ResyCalendar"
      clickableElementIdsToIgnore={clickableElementIdsToIgnore}
      currentMonth={currentMonth}
      numberOfMonths={numberOfMonths}
      renderNavButton={renderNavButton}
      renderDayContents={renderDayContents}
      focusOnClose={focusOnClose}
      closeCalendar={closeCalendar}
      showCloseButton={showCloseButton}
      titleText={titleText}
      venueCity={venueCity}
    />
  );
};

const resyCalendarPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  calendarCache: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      scheduled: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.string,
        inventory: PropTypes.arrayOf(PropTypes.shape({
          event: PropTypes.oneOf(['available', 'not available']),
          reservation: PropTypes.oneOf(['available', 'not available']),
          'walk-in': PropTypes.oneOf(['available', 'not available']),
        })),
      })),
      last_calendar_day: PropTypes.string,
    }),
  }),
  calendarMap: PropTypes.shape({
    // eg 2019-12-16: {event: 'available', reservation: 'available', 'walk-in': 'not available'}
    [PropTypes.string]: PropTypes.shape({
      event: PropTypes.oneOf(['available', 'not available']),
      reservation: PropTypes.oneOf(['available', 'not available']),
      'walk-in': PropTypes.oneOf(['available', 'not available']),
    }),
  }),
  clickableElementIdsToIgnore: PropTypes.arrayOf(PropTypes.string),
  currentMonth: momentPropTypes.momentObj,
  getAriaLabel: PropTypes.func,
  numberOfMonths: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  closeCalendar: PropTypes.func,
  focusOnClose: PropTypes.string.isRequired,
  showCloseButton: PropTypes.bool,
  searchSessionId: PropTypes.string,
  titleText: PropTypes.element,
  venueCity: PropTypes.string.isRequired,
};

ResyCalendar.propTypes = resyCalendarPropTypes;

ResyCalendar.defaultProps = {
  calendarCache: [],
  calendarMap: [],
  clickableElementIdsToIgnore: [],
  closeCalendar: () => { },
  currentMonth: moment(),
  getAriaLabel: () => undefined,
  numberOfMonths: 1,
  showCloseButton: true,
  titleText: null,
  searchSessionId: '',
};

const mapStateToProps = ({ venueSearch }) => ({
  searchSessionId: venueSearch?.metadata?.searchSessionId,
});

ResyCalendar.displayName = 'ResyCalendar';

export { resyCalendarPropTypes };
export default connect(mapStateToProps)(ResyCalendar);
